import { memoryCache } from "@/lib/memorycache"
import { batch, Observable, syncState, when } from "@legendapp/state"

export interface ISortedArray<T> {
  dataMap: Record<string, T>
  sortMap: Record<string, string[]>
  sortReverseMap: Record<string, string[]>
  sorted: Array<T>
  sorted_keyExtractor: (item: T) => string
}

interface ISortItems<T> {
  sortedArray: Observable<ISortedArray<T>>
}
export async function sortItems<T>({ sortedArray }: ISortItems<T>) {
  // Note: Make sure to await the store to load (if it is using an async persistence storage)
  // Don't await for it here but before calling this function
  // const sortedArrayState$ = syncState(sortedArray)
  // await when(sortedArrayState$.isPersistLoaded)

  // TODO: Study if there is a more optimal way to do this loop (maybe we can just do swaps?) with the docs of https://legendapp.com/open-source/state/v3/intro/introduction/
  const sortedItems: Array<T> = []

  // const sortMap = sortedArray.sortMap.get()

  const sortedKeys = Object.keys(sortedArray.sortMap).sort((a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10))

  for (const key of sortedKeys) {
    sortedArray.sortMap[key].forEach((id$) => {
      // @ts-ignore
      const item = sortedArray.dataMap[id$.peek()].peek()
      if (item) {
        sortedItems.push(item)
      }
    })
  }

  sortedArray.assign({ sorted: sortedItems })
}

interface ISetSortedMapItems<T> {
  items: Array<[string, T]>
  sortedArray: Observable<ISortedArray<T>>
  timestampKey: string
  timestampFunction?: (item: T) => number
  idKey: string
  forceSingleSortKeyPerItem?: boolean // Useful when we want to force a single sort key per item (useful for example when we want to sort by timestamp but we want to force a single sort key per item). This is used for example to avoid duplicated contacts
  forceSort?: boolean
}

export async function setSortedMapItems<T extends Record<string, unknown>>({ sortedArray, items, timestampKey, timestampFunction, idKey, forceSingleSortKeyPerItem, forceSort }: ISetSortedMapItems<T>) {
  // Note: Make sure to await the store to load (if it is using an async persistence storage)
  // Don't await for it here but before calling this function

  let needsToSort = forceSort ?? false

  function getTimestampValue(item: T): number {
    if (timestampFunction) {
      return timestampFunction(item)
    }

    // @ts-ignore
    return Number.parseInt(item[timestampKey], 10)
  }

  batch(() => {
    for (const [key, value] of items) {
      const newTimestamp = getTimestampValue(value)
      const currentTimestamp =
        // @ts-ignore
        sortedArray.dataMap[key].get() !== undefined
          ? // @ts-ignore
            getTimestampValue(sortedArray.dataMap[key].get()!)
          : -1

      if (Number.isNaN(newTimestamp) || Number.isNaN(currentTimestamp)) {
        // Ignore the item if the timestamp is not a number
        continue
      }

      if (newTimestamp < currentTimestamp) {
        // Ignore the item if the new timestamp is less than the current timestamp
        continue
      }

      // @ts-ignore
      sortedArray.dataMap[key].set(value)

      const dataTimestamp: number = getTimestampValue(value)

      const dataID = typeof value[idKey] === "string" ? (value[idKey] as string) : `${value[idKey]}`

      //
      if (forceSingleSortKeyPerItem) {
        // Remove the item from the sortMap if it exists by looking for the dataID
        // with the reverseMap
        const reverseMapData = sortedArray.sortReverseMap[`${key}`].peek()
        if (reverseMapData) {
          // Remove every dataID from the sortMap
          batch(() => {
            reverseMapData.forEach((timestamp) => {
              const timestampSortData = sortedArray.sortMap[`${timestamp}`].peek()
              if (timestampSortData) {
                const newTimestampSortData = timestampSortData.filter((id) => id !== key)
                if (newTimestampSortData.length === 0) {
                  sortedArray.sortMap[`${timestamp}`].delete()
                } else {
                  sortedArray.sortMap[`${timestamp}`].set(newTimestampSortData)
                }
              }
            })
          })
        }
      }
      //

      const timestampSortData = sortedArray.sortMap[`${dataTimestamp}`].peek()
      if (!timestampSortData || timestampSortData.length === 0) {
        sortedArray.sortMap[`${dataTimestamp}`].set([key])
        // Add it to the reverseMap
        sortedArray.sortReverseMap[`${key}`].set([`${dataTimestamp}`])
        needsToSort = true
      } else if (!timestampSortData.includes(dataID)) {
        sortedArray.sortMap[`${dataTimestamp}`].push(dataID)
        // Add it to the reverseMap
        sortedArray.sortReverseMap[`${key}`].push(`${dataTimestamp}`)
        needsToSort = true
      } else {
        // There is nothing to do since the data is already in the sortMap
      }
    }
  })

  if (needsToSort) {
    // Apply the changes
    await sortItems({ sortedArray })
  }
}

interface IRemoveSortedMapItem<T> {
  key: string
  sortedArray: Observable<ISortedArray<T>>
  timestampKey: string
  timestampFunction?: (item: T) => number
  idKey: string
}

export async function removeSortedMapItem<T extends Record<string, unknown>>({ idKey, key, sortedArray, timestampKey, timestampFunction }: IRemoveSortedMapItem<T>) {
  function getTimestampValue(item: T): number {
    if (timestampFunction) {
      return timestampFunction(item)
    }

    // @ts-ignore
    return Number.parseInt(item[timestampKey], 10)
  }

  const sortedArrayState$ = syncState(sortedArray)
  await when(sortedArrayState$.isPersistLoaded)

  // @ts-ignore
  if (!sortedArray.dataMap[key].get()) return

  // @ts-ignore
  const itemValue = sortedArray.dataMap[key].get()! // We know that the key exists, we checked it above
  const dataTimestamp: number = getTimestampValue(itemValue)
  const dataID = typeof itemValue[idKey] === "string" ? (itemValue[idKey] as string) : `${itemValue[idKey]}`

  // Remove it from the sortMap
  if (sortedArray.sortMap[`${dataTimestamp}`].get()) {
    // If we could have the guarantee that there is only one item with the dataID id, we could delete using the findIndex logic as explained here (https://legendapp.com/open-source/state/v3/usage/observable/#arrays) but since we can't (I think), we need to filter the array
    const timestampSortData = sortedArray.sortMap[`${dataTimestamp}`]
      .get() // We know that the key exists, we checked it above
      // .get(dataTimestamp)!
      // .get() // We know that the key exists, we checked it above
      .filter((id) => id !== dataID.toString())
    if (timestampSortData.length === 0) {
      sortedArray.sortMap[`${dataTimestamp}`].delete()
    } else {
      sortedArray.sortMap[`${dataTimestamp}`].set(timestampSortData)
    }
  }

  // Remove it from the dataMap
  // @ts-ignore
  sortedArray.dataMap[key].delete()

  // Apply the changes
  await sortItems({ sortedArray })
}

export class SortedMap<T extends Record<string, unknown>> {
  private dataMap: Map<string, T>
  private sortMap: Map<number, string[]>

  private readonly timestampKey: string
  private readonly timestampFunction?: (item: T) => number
  private readonly idKey: string
  private readonly cacheKey: string

  private getTimestampValue(item: T): number {
    if (this.timestampFunction) {
      return this.timestampFunction(item)
    }

    // @ts-ignore
    return Number.parseInt(item[this.timestampKey], 10)
  }

  constructor({ idKey, timestampKey, cacheKey, timestampFunction }: { idKey: string; timestampKey: string; cacheKey?: string; timestampFunction?: (item: T) => number }) {
    this.timestampKey = timestampKey
    this.timestampFunction = timestampFunction
    this.idKey = idKey

    if (cacheKey) {
      this.cacheKey = cacheKey
    } else {
      this.cacheKey = crypto.randomUUID()
    }

    this.dataMap = new Map()
    this.sortMap = new Map()
  }

  public set(key: string, value: T): void {
    this.dataMap.set(key, value)

    const dataTimestamp: number = this.getTimestampValue(value)
    const dataID = typeof value[this.idKey] === "string" ? (value[this.idKey] as string) : `${value[this.idKey]}`

    const timestampSortData = this.sortMap.get(dataTimestamp)

    if (!timestampSortData) {
      this.sortMap.set(dataTimestamp, [key])
    } else if (!timestampSortData.includes(dataID.toString())) {
      this.sortMap.set(dataTimestamp, [...timestampSortData, dataID.toString()])
    } else {
      // There is nothing to do since the data is already in the sortMap
    }

    // Invalidate the cache
    memoryCache.invalidate(this.cacheKey)
  }

  public get(key: string): T | undefined {
    return this.dataMap.get(key)
  }

  public remove(key: string) {
    if (!this.dataMap.has(key)) return

    const itemValue = this.dataMap.get(key)! // We know that the key exists, we checked it above
    const dataTimestamp: number = this.getTimestampValue(itemValue)
    const dataID = typeof itemValue[this.idKey] === "string" ? (itemValue[this.idKey] as string) : `${itemValue[this.idKey]}`

    // Remove it from the sortMap
    if (this.sortMap.has(dataTimestamp)) {
      const timestampSortData = this.sortMap
        .get(dataTimestamp)! // We know that the key exists, we checked it above
        .filter((id) => id !== dataID.toString())
      if (timestampSortData.length === 0) {
        this.sortMap.delete(dataTimestamp)
      } else {
        this.sortMap.set(dataTimestamp, timestampSortData)
      }
    }

    // Remove it from the dataMap
    this.dataMap.delete(key)

    // Invalidate the cache
    memoryCache.invalidate(this.cacheKey)
  }

  public getSortedItems(): Array<[string, T]> {
    const cache = memoryCache.get<Array<[string, T]>>(this.cacheKey)
    if (cache) {
      return cache
    }

    const sortedItems: Array<[string, T]> = []

    const sortedKeys = Array.from(this.sortMap.keys()).sort((a, b) => a - b)

    for (const key of sortedKeys) {
      const ids = this.sortMap.get(key)!
      for (const id of ids) {
        const item = this.dataMap.get(id)
        if (item) {
          sortedItems.push([id, item])
        }
      }
    }

    memoryCache.set(this.cacheKey, sortedItems)

    return sortedItems
  }
}
